import type { StrapiPreservationGridRow } from '../../api/strapiCrm';
import type { StrapiUser } from '../../api/strapi';

function userTypeCode(user: StrapiUser | null | undefined): string {
  return user?.userTypeCode?.trim().toLowerCase() ?? '';
}

function userTypeName(user: StrapiUser | null | undefined): string {
  return user?.userTypeName?.trim().toLowerCase() ?? '';
}

function isAdminPenerimaSample(user: StrapiUser | null | undefined): boolean {
  const code = userTypeCode(user);
  if (code === 'admin_penerima_sample' || code === 'superadmin') return true;
  const name = userTypeName(user);
  return name.includes('penerima sample') || name.includes('penerima sampel');
}

export function isPetugasSampling(user: StrapiUser | null | undefined): boolean {
  return userTypeCode(user) === 'petugas_sampling';
}

export function isSpvLaboratorium(user: StrapiUser | null | undefined): boolean {
  const code = userTypeCode(user);
  return code === 'spv_laboratorium' || code === 'superadmin';
}

export function isAdminFormSampling(user: StrapiUser | null | undefined): boolean {
  const code = userTypeCode(user);
  return code === 'admin_form_sampling' || code === 'superadmin';
}

function isAdminFormSamplingOnly(user: StrapiUser | null | undefined): boolean {
  return userTypeCode(user) === 'admin_form_sampling';
}

export function canShowVerifyFormSampling(
  row: StrapiPreservationGridRow,
  user?: StrapiUser | null,
): boolean {
  return isAdminFormSampling(user) && row.signed_points > 0;
}

export function formSamplingAllVerified(row: StrapiPreservationGridRow): boolean {
  if (row.signed_points <= 0) return false;
  return (row.verified_points ?? 0) >= row.signed_points;
}

/** Row yang sudah terverifikasi penuh hanya bisa dibuka untuk dilihat. */
export function canShowViewVerifiedFormSampling(
  row: StrapiPreservationGridRow,
  user?: StrapiUser | null,
): boolean {
  return isAdminFormSampling(user) && formSamplingAllVerified(row);
}

/** Titik yang perlu diterima kantor = BA signed (bukan cancelled). */
function receivablePointCount(row: StrapiPreservationGridRow): number {
  return Math.max(row.signed_points || 0, 0);
}

export function receivedByAdminLabel(row: StrapiPreservationGridRow): string {
  const handed = row.handed_over_points ?? 0;
  const total = receivablePointCount(row);
  if (handed <= 0 || total <= 0) return '—';
  return `${handed} / ${total}`;
}

export function handedOverPointsComplete(row: StrapiPreservationGridRow): boolean {
  const total = receivablePointCount(row);
  if (total <= 0) return false;
  return (row.handed_over_points ?? 0) >= total;
}

/** SPV lab only sees row actions at tahap Diterima kantor with every point handed over. */
export function isReceivedAtOfficeWithCompleteHandover(row: StrapiPreservationGridRow): boolean {
  return row.workflow_step === 'received' && handedOverPointsComplete(row);
}

function isSpvLaboratoriumOnly(user: StrapiUser | null | undefined): boolean {
  return userTypeCode(user) === 'spv_laboratorium';
}

function rowHasUnreceivedPoints(row: StrapiPreservationGridRow): boolean {
  const total = receivablePointCount(row);
  if (total <= 0) return false;
  return (row.handed_over_points ?? 0) < total;
}

/** Diterima kantor vs masuk ke kantor (titik BA signed). Tidak sama = masih boleh diterima lagi. */
function receivedOfficeCountsDiffer(row: StrapiPreservationGridRow): boolean {
  const received = Math.max(row.handed_over_points ?? 0, 0);
  const incoming = receivablePointCount(row);
  if (received <= 0 && incoming <= 0) return false;
  return received !== incoming;
}

/**
 * Admin penerima sample dapat menerima selama masih ada titik berita acara
 * yang belum di-handover (termasuk saat status all completed / siap serah terima).
 */
export function rowReadyForSampleReceived(row: StrapiPreservationGridRow): boolean {
  return rowHasUnreceivedPoints(row);
}

export function quotationAllowsSampleReceive(status?: string | null): boolean {
  const s = String(status ?? '').trim();
  if (!s) return true;
  return s === 'customer_approved';
}

export function sampleReceiveBlockedByQuotationReason(status?: string | null): string | null {
  if (quotationAllowsSampleReceive(status)) return null;
  return 'Penerimaan sampel menunggu persetujuan marketing (PIC → SPV → manajer bila ≥ Rp 20 juta → pelanggan).';
}

export function sampleReceiveBlockedReason(
  row: StrapiPreservationGridRow,
): string | null {
  if (row.sample_receive_blocked_reason?.trim()) return row.sample_receive_blocked_reason.trim();
  return sampleReceiveBlockedByQuotationReason(row.quotation_status);
}

export function canSampleReceived(
  row: StrapiPreservationGridRow,
  user?: StrapiUser | null,
): boolean {
  if (isAdminFormSamplingOnly(user)) return false;
  if (isAdminPenerimaSample(user) && receivedOfficeCountsDiffer(row)) return true;
  if (row.can_sample_received === false) return false;
  if (sampleReceiveBlockedReason(row)) return false;
  if (row.can_sample_received === true) return true;
  if (!isAdminPenerimaSample(user)) return false;
  return rowReadyForSampleReceived(row);
}

export function canShowBlockedSampleReceived(
  row: StrapiPreservationGridRow,
  user?: StrapiUser | null,
): boolean {
  if (isAdminFormSamplingOnly(user)) return false;
  if (canSampleReceived(row, user)) return false;
  if (!isAdminPenerimaSample(user) && row.can_sample_received !== true) return false;
  if (!rowReadyForSampleReceived(row)) return false;
  return Boolean(sampleReceiveBlockedReason(row));
}

function beritaAcaraAllCompleted(row: StrapiPreservationGridRow): boolean {
  return row.completion_status === 'all_completed';
}

export function canGenerateRpsPdf(
  row: StrapiPreservationGridRow,
  user?: StrapiUser | null,
): boolean {
  if (row.completion_status === 'partial_completed') return false;
  if (isPetugasSampling(user)) return false;
  if (isAdminFormSamplingOnly(user)) return false;
  if (isSpvLaboratoriumOnly(user)) return beritaAcaraAllCompleted(row);
  return true;
}

export function canViewReceiveLogs(user?: StrapiUser | null): boolean {
  return !isSpvLaboratoriumOnly(user) && !isAdminFormSamplingOnly(user);
}

/** PDF berita acara yang sudah terbit (TTD petugas dan pelanggan), sama seperti print petugas sampling. */
export function canPrintIssuedBeritaAcara(
  row: StrapiPreservationGridRow,
  user?: StrapiUser | null,
): boolean {
  if ((row.signed_points ?? 0) <= 0) return false;
  return isAdminPenerimaSample(user);
}

function rpsReadyForReview(row: StrapiPreservationGridRow): boolean {
  return row.completion_status === 'all_completed';
}

export function canApproveRps(
  row: StrapiPreservationGridRow,
  user?: StrapiUser | null,
): boolean {
  if (!isSpvLaboratorium(user)) return false;
  if (!rpsReadyForReview(row)) return false;
  return row.rps_status !== 'approved';
}

export function canRejectRps(
  row: StrapiPreservationGridRow,
  user?: StrapiUser | null,
): boolean {
  if (!isSpvLaboratorium(user)) return false;
  if (!rpsReadyForReview(row)) return false;
  return row.rps_status !== 'approved' && row.rps_status !== 'rejected';
}

export function hasVisiblePreservationActions(
  row: StrapiPreservationGridRow,
  user?: StrapiUser | null,
): boolean {
  return (
    canViewReceiveLogs(user) ||
    canSampleReceived(row, user) ||
    canShowBlockedSampleReceived(row, user) ||
    canGenerateRpsPdf(row, user) ||
    canPrintIssuedBeritaAcara(row, user) ||
    canApproveRps(row, user) ||
    canRejectRps(row, user) ||
    canShowVerifyFormSampling(row, user) ||
    canShowViewVerifiedFormSampling(row, user)
  );
}
